/**
 * Ordering the inventory by what needs attention.
 *
 * The list is not alphabetical and not by location, because neither answers the question
 * someone actually opens the app with: what do I need to use up? Everything about to lapse
 * comes first, and the things that keep indefinitely sink to the bottom where they belong.
 */

import type { InventoryItem } from '../api/types';
import { isOutOfStock } from './levels';

/** How far ahead "soon" looks, matching the digest's default window. */
export const SOON_DAYS = 3;

export type Band =
  /** Past its date and waiting to be confirmed gone. */
  | 'overdue'
  /** Due inside the window. This is the band the whole app exists to surface. */
  | 'soon'
  /** Dated, but not yet worth thinking about. */
  | 'later'
  /** Staples and anything the catalog has no opinion about. */
  | 'keeps'
  /** Run out of, and kept so that one tap says it is back. */
  | 'out';

export interface Group {
  band: Band;
  heading: string;
  items: InventoryItem[];
}

const HEADINGS: Record<Band, string> = {
  overdue: 'Use now',
  soon: 'Use this week',
  later: 'Later',
  keeps: 'Keeps',
  out: 'Out of stock',
};

/**
 * Bands as the screen reads, top to bottom.
 *
 * What is out sits below even the staples, because it is the one band that is not about food
 * in the house. It is the household's own list of things they buy and have none of — the
 * fastest way to say a food is back, kept out of the way of everything that is actually here.
 */
const ORDER: readonly Band[] = ['overdue', 'soon', 'later', 'keeps', 'out'];

export function bandFor(item: InventoryItem, now: Date): Band {
  // Asked before the dates, because an empty line's dates are about food that is gone.
  if (isOutOfStock(item)) {
    return 'out';
  }

  if (item.expires_at === null || item.is_staple) {
    return 'keeps';
  }

  const dueIn = daysUntil(item.expires_at, now);
  if (dueIn < 0 || item.state === 'probably_gone') {
    return 'overdue';
  }

  return dueIn <= SOON_DAYS ? 'soon' : 'later';
}

/**
 * The inventory as it should be read, soonest first, with empty bands left out.
 *
 * Items that are gone never appear — they are history, and history lives in the log.
 */
export function grouped(items: InventoryItem[], now: Date): Group[] {
  const bands = new Map<Band, InventoryItem[]>();

  for (const item of items) {
    if (item.state === 'gone') {
      continue;
    }
    const band = bandFor(item, now);
    bands.set(band, [...(bands.get(band) ?? []), item]);
  }

  return ORDER.flatMap((band) => {
    const held = bands.get(band);
    if (held === undefined || held.length === 0) {
      return [];
    }

    // Everything else is ordered by what needs using; what is out has nothing to use, so it
    // is ordered by what is findable.
    const order = band === 'out' ? byName : soonestFirst;

    return [{ band, heading: HEADINGS[band], items: [...held].sort(order) }];
  });
}

/** Whole days from `now` until the date, rounded down; negative once it has passed. */
export function daysUntil(when: string, now: Date): number {
  const millis = new Date(when).getTime() - now.getTime();

  return Math.floor(millis / 86_400_000);
}

/** How the date should read on a row: short, and never a date the user has to decode. */
export function dueWording(item: InventoryItem, now: Date): string {
  // A date on an empty line describes food that is gone. "5 days ago" under a heading saying
  // the household has none of it is noise at best and a claim about nothing at worst.
  if (isOutOfStock(item)) {
    return '';
  }
  if (item.is_staple) {
    return 'staple';
  }
  if (item.expires_at === null) {
    return '';
  }

  const dueIn = daysUntil(item.expires_at, now);
  if (dueIn < -1) {
    return `${Math.abs(dueIn)} days ago`;
  }
  if (dueIn < 0) {
    return 'yesterday';
  }
  if (dueIn === 0) {
    return 'today';
  }
  if (dueIn === 1) {
    return 'tomorrow';
  }

  return `${dueIn} days`;
}

function byName(left: InventoryItem, right: InventoryItem): number {
  return left.name.localeCompare(right.name);
}

function soonestFirst(left: InventoryItem, right: InventoryItem): number {
  // Undated items sort last within their band; they are the ones with nothing pressing.
  const leftDue = left.expires_at ?? '9999';
  const rightDue = right.expires_at ?? '9999';

  return leftDue === rightDue
    ? left.name.localeCompare(right.name)
    : leftDue < rightDue
      ? -1
      : 1;
}
